// /mfa — second-factor verification (design/look-and-feel/11-entry-flows.html).
// Copy strings are the contract asserted by e2e/login.spec.ts ("Verificación de
// 2 pasos", "Código", "Verificar") — keep them.
//
// Two modes: TOTP (default, 6-digit) and backup code (free-text), toggled via the
// "Usa un código de respaldo" link. Guard: no challenge token → redirect to /login.

import { type FormEvent, useEffect, useRef, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'

import { postMfaVerify } from '../api/client.js'
import { describeError } from '../api/describe-error.js'
import { contextFromToken, homePathForContext } from '../auth/session.js'
import { useAuthStore } from '../auth/store.js'
import { Alert, Button, FormField, Input } from '../design-system/index.js'
import { AuthLayout } from './auth/AuthLayout.js'

export function MfaPage(): JSX.Element {
  const navigate = useNavigate()
  const challengeToken = useAuthStore((s) => s.mfaChallengeToken)
  const setSession = useAuthStore((s) => s.setSession)

  // Capture the challenge on mount so the guard below doesn't redirect us
  // mid-flight: setSession clears mfaChallengeToken, which would otherwise
  // re-trigger the guard before navigate('/home') resolves.
  const initialChallengeRef = useRef(challengeToken)

  const [code, setCode] = useState('')
  const [isBackup, setIsBackup] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  // Reset code when toggling between modes (digits-only vs free-text).
  useEffect(() => {
    setCode('')
    setError(null)
  }, [isBackup])

  if (initialChallengeRef.current === null) {
    return <Navigate to="/login" replace />
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault()
    const token = initialChallengeRef.current
    if (token === null) return
    setError(null)
    setSubmitting(true)

    try {
      const result = await postMfaVerify(token, code.trim(), isBackup)
      setSession({
        accessToken: result.data.access_token,
        refreshToken: result.data.refresh_token,
        user: result.data.user,
      })
      // Same rule as Login: staff land on /admin, clients on /home. The
      // hardcoded '/home' sent operators with MFA to a page that redirected
      // them back out — a wrong turn on every single login.
      void navigate(homePathForContext(contextFromToken(result.data.access_token)), {
        replace: true,
      })
    } catch (err) {
      setError(describeError(err))
    } finally {
      setSubmitting(false)
    }
  }

  // Validation: TOTP must be exactly 6 digits; backup must be non-empty.
  const ready = isBackup ? code.trim().length > 0 : /^\d{6}$/.test(code)

  return (
    <AuthLayout>
      <h1 className="text-[26px] font-bold tracking-[-0.025em] text-text">
        Verificación de 2 pasos
      </h1>
      <p className="mt-1.5 text-[13px] text-text-2">
        {isBackup
          ? 'Ingresa uno de tus códigos de respaldo.'
          : 'Ingresa el código de 6 dígitos de tu app de autenticación.'}
      </p>

      {error !== null && (
        <Alert tone="sens" className="mt-5">
          {error}
        </Alert>
      )}

      <form onSubmit={(e) => void onSubmit(e)} className="mt-6 flex flex-col gap-4" noValidate>
        <FormField label={isBackup ? 'Código de respaldo' : 'Código'} htmlFor="code">
          <Input
            inputMode={isBackup ? 'text' : 'numeric'}
            autoComplete={isBackup ? 'off' : 'one-time-code'}
            autoFocus
            required
            value={code}
            onChange={(e) => setCode(e.target.value)}
            disabled={submitting}
            maxLength={isBackup ? 32 : 6}
            placeholder={isBackup ? 'aaaa-bbbb' : '123456'}
            className="text-center font-mono tracking-[0.3em]"
          />
        </FormField>

        <Button type="submit" block loading={submitting} disabled={!ready}>
          Verificar
        </Button>
      </form>

      {/* Checkpoint-1 finding: at 11.5px tertiary gray this was invisible at
          the exact moment someone without their app needs it — and since bad
          MFA codes now feed the account lockout, guessing is expensive. Make
          the escape hatch legible. */}
      <button
        type="button"
        onClick={() => setIsBackup(!isBackup)}
        className="mt-5 block w-full rounded text-center text-[12.5px] font-semibold text-link hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-blue"
      >
        {isBackup
          ? '← Volver al código de la app'
          : '¿Perdiste tu app de autenticación? Usa un código de respaldo →'}
      </button>
    </AuthLayout>
  )
}
